import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { reconciliationService, getErrorMessage } from '@/services';
import { uploadOutcome } from '@/pages/commissions/statementUpload';
import type { DifferenceReportQuery } from '@/services/reconciliation';

export function useStatementLines(page = 1, status?: string) {
  return useQuery({
    queryKey: ['statementLines', page, status],
    queryFn: () => reconciliationService.listStatementLines({ page, status }),
    placeholderData: (prev) => prev,
  });
}

/**
 * 3e: the corrections behind one line's current difference.
 *
 * Fetched only when a line is actually open, and keyed by line id so two
 * drawers opened in a row never show each other's history. The query is left
 * to report its own loading and error states -- the drawer renders all three,
 * because "we could not load this line's history" and "this line was never
 * corrected" are different facts and this screen has shipped that confusion
 * before.
 */
export function useStatementLineRevisions(lineId: string | null) {
  return useQuery({
    queryKey: ['statementLineRevisions', lineId],
    queryFn: () => reconciliationService.listStatementLineRevisions(lineId!),
    enabled: Boolean(lineId),
  });
}

/**
 * STMT: the difference report for one statement (insurer + period). Only runs
 * once a statement has been chosen — after an upload, or from the picker.
 */
export function useStatementDifferenceReport(query: DifferenceReportQuery | null) {
  return useQuery({
    queryKey: ['statementDifferenceReport', query?.insurer, query?.period],
    queryFn: () => reconciliationService.getDifferenceReport(query!),
    enabled: Boolean(query?.insurer && query?.period),
  });
}

export function useReconciliationSummary() {
  return useQuery({
    queryKey: ['reconciliationSummary'],
    queryFn: () => reconciliationService.getReconciliationSummary(),
  });
}

/**
 * M-02: re-check the broker's lines that are waiting for their policy.
 *
 * The toast reports what the server re-read as STILL pending, so "Re-checked"
 * never reads as "all settled" when nothing matched.
 */
export function useRematchPending() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (insurer?: string) => reconciliationService.rematchPending(insurer),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['statementLines'] });
      qc.invalidateQueries({ queryKey: ['reconciliationSummary'] });
      qc.invalidateQueries({ queryKey: ['statementDifferenceReport'] });
      const settled = data.reconciled + data.disputed;
      const parts = [
        settled
          ? `${settled} line${settled === 1 ? '' : 's'} matched (${data.reconciled} matched, ${data.disputed} mismatched)`
          : 'No pending line found its policy yet',
        `${data.still_pending} still pending`,
      ];
      if (data.failed) {
        toast.error(`${parts.join('; ')}; ${data.failed} could not be checked.`);
      } else {
        toast.success(`${parts.join('; ')}.`);
      }
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

/**
 * Upload an insurer statement.
 *
 * This hook used to toast `"${reconciled} reconciled, ${disputed} disputed,
 * ${unmatched} unmatched"` unconditionally and never look at `errors` — so an
 * upload in which EVERY row was rejected produced a green "0 reconciled, 0
 * disputed, 0 unmatched", indistinguishable from a clean run over an empty
 * file. Rows that did not import are now counted in the toast and listed on
 * the tab; a run with rejections is an error toast, not a success.
 */
export function useUploadStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      insurer,
      statementPeriod,
      format,
      uploadAgain,
    }: {
      file: File;
      insurer?: string;
      /** 3d: the month the statement covers ("YYYY-MM"). */
      statementPeriod?: string;
      /** #436: force a statement layout; omitted = detect from the headers. */
      format?: string;
      uploadAgain?: boolean;
    }) =>
      reconciliationService.uploadStatement(file, insurer, statementPeriod, { format, uploadAgain }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['statementLines'] });
      qc.invalidateQueries({ queryKey: ['reconciliationSummary'] });
      qc.invalidateQueries({ queryKey: ['statementDifferenceReport'] });
      const outcome = uploadOutcome(data);
      if (outcome.failed > 0 || !outcome.imported) {
        toast.error(outcome.message);
      } else {
        toast.success(outcome.message);
      }
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}
